import {
  ButtonHTMLAttributes,
  ClassAttributes,
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react'
import Button from '../Button/Button'

type ButtonProps = ClassAttributes<HTMLButtonElement> &
  ButtonHTMLAttributes<HTMLButtonElement>

interface Props extends ButtonProps {
  contentUpdate: string | ReactNode
  timerToReturnToFirstState?: number
  /** When defined, externally controls the feedback state (ignores internal timer). */
  feedbackActive?: boolean
}

const ButtonWithFeedback: FC<Props> = ({
  contentUpdate,
  timerToReturnToFirstState = 0,
  feedbackActive,
  onClick,
  children,
  title,
  className,
  disabled,
  ...rest
}) => {
  const stateControlled = feedbackActive !== undefined
  const [isClicked, setIsClicked] = useState(false)

  useEffect(() => {
    if (!stateControlled && isClicked && timerToReturnToFirstState > 0) {
      const timer = setTimeout(
        () => setIsClicked(false),
        timerToReturnToFirstState
      )
      return () => {
        clearTimeout(timer)
      }
    }
  }, [isClicked, stateControlled, timerToReturnToFirstState])

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!stateControlled) {
        setIsClicked(true)
      }
      if (onClick) {
        onClick(e)
      }
    },
    [onClick, stateControlled]
  )

  const showFeedback = stateControlled ? feedbackActive : isClicked

  return (
    <Button
      className={className}
      onClick={handleClick}
      title={title}
      type="button"
      disabled={disabled}
      {...rest}
    >
      {showFeedback ? contentUpdate : children}
    </Button>
  )
}

export default ButtonWithFeedback
